import { pushRoute } from '../../lib/router.js';
import React, { useEffect, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { mapFinalFormErrors } from '../../lib/utils.js';

import SubHeader from '../../components/SubHeader.jsx';
import StakeholdersCsvForm from '../../components/organizations/Stakeholders/StakeholdersCsvForm.jsx';
import RadioAdapter from '../../components/RadioAdapter.jsx';
import StakeholdersManuallyForm from '../../components/organizations/Stakeholders/StakeholdersManuallyForm.jsx';
import {
  defaultGroupStakeholder,
  methodOptions,
  onStakeholdersUploadChange,
  replaceOptions,
  validateStakeholdersCsv,
  validateStakeholdersManually,
} from './utils/stakeholdersStructure.js';
import {
  retrieveOrgStakeholders,
  selectOrgStakeholders,
  selectRetrieveOrgStakeholdersState,
  updateOrgStakeholders,
} from '../../modules/orgStakeholders.js';
import { useFetchFile } from '../../hooks/useFetchFile.js';
import { selectCurrentOrganizationId } from '../../modules/auth.js';
import OrganizationHeader from '../../components/headers/PageHeaders/Organization/OrganizationHeader.jsx';
import StakeholdersCurrentCSVDownloadButton from '../../components/StakeholdersCurrentCSVDownloadButton.jsx';
import { getOrgTermsContent } from '../../content/utils.js';

const mapErrors = mapFinalFormErrors('Failed to save');

const mapStateToProps = (state) => ({
  organizationId: selectCurrentOrganizationId(state),
  orgStakeholders: selectOrgStakeholders(state),
  retrieveSuccess: selectRetrieveOrgStakeholdersState(state).success,
  apiUrl: state.config.apiUrl,
});

const mapDispatchToProps = { retrieveOrgStakeholders, updateOrgStakeholders };

const OrgStakeholdersFormContainer = (props) => {
  const {
    slug,
    organizationId,
    retrieveOrgStakeholders,
    updateOrgStakeholders,
    orgStakeholders,
    retrieveSuccess,
    apiUrl,
  } = props;

  const [uploadMethod, setUploadMethod] = useState('manually');
  const [replaceMethod, setReplaceMethod] = useState('add');

  const [stakeholdersStructure, updateStakeholdersStructure] = useState([]);

  const formRef = useRef(null);

  const { downloadBlob } = useFetchFile();

  const onCancel = () => pushRoute('organization/stakeholders');

  const onSubmitCsv = () => {
    const formData = new FormData(formRef.current);
    formData.append('merge', replaceMethod === 'add');

    return updateOrgStakeholders(formData)
      .then(() => pushRoute('organization/stakeholders'))
      .catch(mapErrors);
  };

  const onSubmitManually = () => {
    const resource = {
      manually: true,
      organization_stakeholders: stakeholdersStructure,
    };

    return updateOrgStakeholders(resource)
      .then(() => pushRoute('organization/stakeholders'))
      .catch(mapErrors);
  };

  const onAddNewStakeholder = () =>
    updateStakeholdersStructure([
      ...stakeholdersStructure,
      defaultGroupStakeholder,
    ]);

  const onUpdateStakeholderField = (index, key, value) => {
    updateStakeholdersStructure((prevState) => {
      const updated = [...prevState];
      updated[index] = {
        ...updated[index],
        [key]: value,
      };
      return updated;
    });
  };

  const onDeleteStakeholder = (index) => {
    const { destroyable } = stakeholdersStructure[index];

    if (
      !window.confirm(
        !destroyable
          ? `Are you sure you want to delete this stakeholder? \n\nThis stakeholder is currently being tracked and used in one or more ${getOrgTermsContent('projects')} and reports. When you delete the Stakeholder at the Organization Level, it will be deleted across all ${getOrgTermsContent('projects')} and no longer available for selection in reports.`
          : 'Are you sure you want to delete this stakeholder?'
      )
    )
      return;

    updateStakeholdersStructure((prevArray) => {
      const updated = [...prevArray];
      if (index >= 0 && index < updated.length) {
        updated.splice(index, 1);
      }
      return updated;
    });
  };

  const onDownloadCsv = () => {
    downloadBlob(
      apiUrl,
      `organizations/${organizationId}/organization_stakeholders/structure_csv`,
      null,
      'current-organization-structure CSV',
      'csv'
    );
  };

  const renderUploadRadio = () =>
    methodOptions.map(({ label, value }) => (
      <Field
        key={value}
        name="isManually"
        component={RadioAdapter}
        label={label}
        type="radio"
        value={value}
        checked={uploadMethod === value}
        onChange={() => onStakeholdersUploadChange(value, setUploadMethod)}
      />
    ));

  const renderReplaceRadio = () =>
    replaceOptions.map(({ label, value }) => (
      <Field
        key={value}
        name="isReplace"
        component={RadioAdapter}
        label={label}
        type="radio"
        value={value}
        checked={replaceMethod === value}
        onChange={() => setReplaceMethod(value)}
      />
    ));

  useEffect(() => {
    retrieveOrgStakeholders();
  }, []);

  useEffect(() => {
    retrieveSuccess && updateStakeholdersStructure(orgStakeholders);
  }, [retrieveSuccess]);

  return (
    <React.Fragment>
      <OrganizationHeader slug={slug} />

      <SubHeader flexJustify small>
        <h2>Edit Organization Stakeholders</h2>
      </SubHeader>
      {retrieveSuccess ? (
        <Grid>
          <Row>
            <Col sm={12}>
              <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Row className="m-b-48">
                      <Col xs={12}>
                        <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
                          <h3>Edit Stakeholders Structure</h3>
                        </div>
                        <p className="communication-help text-muted">
                          Upload a CSV or add stakeholders manually.
                        </p>
                      </Col>
                      <Col xs={10}>
                        <Form
                          onSubmit={() => {}}
                          component={renderUploadRadio}
                        />
                      </Col>
                    </Row>

                    {stakeholdersStructure.length && uploadMethod === 'csv' ? (
                      <Row className="m-b-60">
                        <Col xs={12}>
                          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
                            <h3>Existing Stakeholder Structure</h3>
                          </div>
                        </Col>
                        <Col xs={10}>
                          <div className="m-b-18">
                            <Form
                              onSubmit={() => {}}
                              component={renderReplaceRadio}
                            />
                          </div>
                          <StakeholdersCurrentCSVDownloadButton
                            onDownloadCsv={onDownloadCsv}
                          />
                        </Col>
                      </Row>
                    ) : null}

                    {uploadMethod === 'csv' ? (
                      <Form
                        component={StakeholdersCsvForm}
                        onSubmit={onSubmitCsv}
                        validate={() => validateStakeholdersCsv(formRef)}
                        onCancel={onCancel}
                        formRef={formRef}
                      />
                    ) : null}

                    {uploadMethod === 'manually' ? (
                      <Form
                        component={StakeholdersManuallyForm}
                        onSubmit={onSubmitManually}
                        validate={() =>
                          validateStakeholdersManually(stakeholdersStructure)
                        }
                        stakeholdersStructure={stakeholdersStructure}
                        onAddNewStakeholder={onAddNewStakeholder}
                        onUpdateStakeholderField={onUpdateStakeholderField}
                        onDeleteStakeholder={onDeleteStakeholder}
                        onCancel={onCancel}
                      />
                    ) : null}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </Grid>
      ) : null}
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OrgStakeholdersFormContainer);
